import { useCallback, useEffect, useState } from 'react';
import { getText } from 'i18n/intl';
import { calendarClient } from 'services/calendarClient';
import { getErrorMessage } from 'utils/errorUtils';
import { errorNotification } from 'utils/notifications';
import type {
  EventMappingResult,
  GetOutlookEventQuery,
} from '@timeedit/calendar-integration-api-contract';
import { Label, TEInput } from '@timeedit/ui-components';
import { Space, Spin } from '@timeedit/ui-components/antd';
import Paragraph from '@timeedit/ui-components/antd/typography/Paragraph';

type EventMappingDetailProps = {
  eventMapping?: EventMappingResult;
};

export const EventMappingDetail = ({
  eventMapping,
}: EventMappingDetailProps) => {
  const [outlookEvent, setOutlookEvent] = useState<unknown>();
  const [loading, setLoading] = useState(false);

  const fetchOutlookEvent = useCallback(async (query: GetOutlookEventQuery) => {
    try {
      setLoading(true);
      const response = await calendarClient.eventMapping.getOutlookEvent({
        query,
      });
      if (response.status !== 200) {
        setOutlookEvent(undefined);
        return errorNotification(getErrorMessage(response));
      }

      setOutlookEvent(response.body.requestedEvent.event);
    } catch (error) {
      errorNotification(
        getErrorMessage(error) || getText('integration.calendar.logs.error')
      );
      setOutlookEvent(undefined);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (eventMapping) {
      fetchOutlookEvent({
        eventId: eventMapping.eventId,
        userId: eventMapping.userPrincipalName,
      });
    }
  }, [eventMapping, fetchOutlookEvent]);

  if (!eventMapping) {
    return null;
  }
  return (
    <Space direction="vertical">
      <TEInput.PureText
        label="Reservation id"
        value={eventMapping.reservationId.toString()}
      />
      <TEInput.PureText label="Email" value={eventMapping.userPrincipalName} />
      <TEInput.PureText
        label="Reservation begin"
        value={eventMapping.begin.toString()}
      />
      <TEInput.PureText
        label="Last update"
        value={eventMapping.updatedAt.toString()}
      />
      <TEInput.PureText
        label="Event type"
        value={eventMapping.isRecurring ? 'Recurring' : 'Single'}
      />
      <TEInput.PureText label="Sync status" value={eventMapping.syncStatus} />
      <TEInput.PureText label="Outlook event id" value={eventMapping.eventId} />
      <Label title="Outlook Event Data" />
      <Paragraph>
        <pre>
          {loading ? (
            <Spin />
          ) : outlookEvent ? (
            JSON.stringify(outlookEvent, null, 2)
          ) : (
            'Not found'
          )}
        </pre>
      </Paragraph>
    </Space>
  );
};
